import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import { I18nProvider } from "@/lib/saas_i18n";
import { Toaster } from "@/components/ui/sonner";
import PublicLayout from "@/components/layout/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import InstallPrompt, { PwaUpdateToast } from "@/pwa/InstallPrompt";

// Eager: LCP + auth entry points
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";

// Lazy: everything else (route-level code splitting)
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const EquipmentDetail = lazy(() => import("@/pages/EquipmentDetail"));
const Auctions = lazy(() => import("@/pages/Auctions"));
const AuctionDetail = lazy(() => import("@/pages/AuctionDetail"));
const PostRequirement = lazy(() => import("@/pages/PostRequirement"));
const BecomeSupplier = lazy(() => import("@/pages/BecomeSupplier"));
const BecomeScrapBuyer = lazy(() => import("@/pages/BecomeScrapBuyer"));
const AdminScrapBuyers = lazy(() => import("@/pages/AdminScrapBuyers"));
const AdminClients = lazy(() => import("@/pages/AdminClients"));
const AdminClientDetail = lazy(() => import("@/pages/AdminClientDetail"));
const AdminClientsBulkImport = lazy(() => import("@/pages/AdminClientsBulkImport"));
const AdminClientAnalytics = lazy(() => import("@/pages/AdminClientAnalytics"));
const AdminRequirementEntry = lazy(() => import("@/pages/AdminRequirementEntry"));
const Connect = lazy(() => import("@/pages/Connect"));
const ConnectDashboard = lazy(() => import("@/pages/ConnectDashboard"));
const ConnectBranding = lazy(() => import("@/pages/ConnectBranding"));
const ConnectCRM = lazy(() => import("@/pages/ConnectCRM"));
const SupplierBosHub = lazy(() => import("@/pages/SupplierBosHub"));
const AdminConnectPlans = lazy(() => import("@/pages/AdminConnectPlans"));
const Careers = lazy(() => import("@/pages/Careers"));
const AdminCareers = lazy(() => import("@/pages/AdminCareers"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const BlogList = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogList })));
const BlogPost = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogPost })));
const BuyEquipment = lazy(() => import("@/pages/BuyEquipment"));
const SellEquipment = lazy(() => import("@/pages/SellEquipment"));
const ReverseAuction = lazy(() => import("@/pages/ReverseAuction"));
const Categories = lazy(() => import("@/pages/Categories"));
const CategoryLanding = lazy(() => import("@/pages/CategoryLanding"));
const Privacy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const RefundPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.RefundPolicy })));
const SupplierAgreement = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.SupplierAgreement })));
const MissionStatement = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.MissionStatement })));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const SelectorPage = lazy(() => import("@/pages/SelectorPage"));
const BuyNewMarketplace = lazy(() => import("@/pages/BuyNew").then((m) => ({ default: m.BuyNewMarketplace })));
const BuyNewDetail = lazy(() => import("@/pages/BuyNew").then((m) => ({ default: m.BuyNewDetail })));
const BecomeDealer = lazy(() => import("@/pages/BecomeDealer"));
const OEMPartners = lazy(() => import("@/pages/OEMPartners"));
const DealerDashboard = lazy(() => import("@/pages/DealerDashboard"));
const BuyerQuoteComparison = lazy(() => import("@/pages/BuyerQuoteComparison"));
const AdminBuyNew = lazy(() => import("@/pages/AdminBuyNew").then((m) => ({ default: m.AdminBuyNew })));
const UniversalSearch = lazy(() => import("@/pages/UniversalSearch"));
const Workforce = lazy(() => import("@/pages/Workforce"));
const Inspection = lazy(() => import("@/pages/Inspection"));
const RequestInspection = lazy(() => import("@/pages/RequestInspection"));
const BecomeInspectionPartner = lazy(() => import("@/pages/BecomeInspectionPartner"));
const InspectionProviders = lazy(() => import("@/pages/InspectionProviders"));
const SparesLanding = lazy(() => import("@/pages/Spares").then((m) => ({ default: m.SparesLanding })));
const SparesRequest = lazy(() => import("@/pages/Spares").then((m) => ({ default: m.SparesRequest })));
const SparesSupplierRegister = lazy(() => import("@/pages/Spares").then((m) => ({ default: m.SparesSupplierRegister })));
const SuppliersDirectory = lazy(() => import("@/pages/SuppliersDirectory"));
const SaasSignup = lazy(() => import("@/pages/SaasSignup"));
const SaasPricing = lazy(() => import("@/pages/SaasPricing"));
const ComingSoonIndex = lazy(() => import("@/pages/ComingSoon").then((m) => ({ default: m.ComingSoonIndex })));
const ComingSoonDetail = lazy(() => import("@/pages/ComingSoon").then((m) => ({ default: m.ComingSoonDetail })));

// Admin — lazy (large chunk, ~2900 lines in AdminDashboard alone)
const AdminOnboard = lazy(() => import("@/pages/AdminOnboard").then((m) => ({ default: m.AdminOnboard })));
const AdminApprovals = lazy(() => import("@/pages/AdminApprovals"));
const AdminAmc = lazy(() => import("@/pages/AdminAmc"));
const AdminBreakdown = lazy(() => import("@/pages/AdminBreakdown"));
const AdminIntel = lazy(() => import("@/pages/AdminIntel"));
const AmcPartnerOnboard = lazy(() => import("@/pages/AmcPartnerOnboard"));
const BreakdownPartnerOnboard = lazy(() => import("@/pages/BreakdownPartnerOnboard"));
const IntelDirectory = lazy(() => import("@/pages/Intel").then(m => ({ default: m.IntelDirectory })));
const IntelReader = lazy(() => import("@/pages/Intel").then(m => ({ default: m.IntelReader })));
const AdminTeam = lazy(() => import("@/pages/AdminTeam"));
const AdminEmployees = lazy(() => import("@/pages/AdminEmployees"));
const AdminBulkUpload = lazy(() => import("@/pages/AdminBulkUpload"));
const AdminInvoices = lazy(() => import("@/pages/AdminInvoices"));
const AdminPermissions = lazy(() => import("@/pages/AdminPermissions"));
const AdminAccessRequests = lazy(() => import("@/pages/AdminAccessRequests"));
const AdminOffers = lazy(() => import("@/pages/AdminOffers"));
const AdminAds = lazy(() => import("@/pages/AdminAds"));
const AdminCredits = lazy(() => import("@/pages/AdminCredits"));
const AdminConfig = lazy(() => import("@/pages/AdminConfig"));
const AdminCrmAutomation = lazy(() => import("@/pages/admin/CrmAutomation"));
const AdminAiWorkforce = lazy(() => import("@/pages/AdminAiWorkforce"));
const AdminSecurity = lazy(() => import("@/pages/AdminSecurity"));
const AdminHealthCheck = lazy(() => import("@/pages/AdminHealthCheck"));
const AdminFoundation = lazy(() => import("@/pages/AdminFoundation"));
const AdminHrms = lazy(() => import("@/pages/AdminHrms"));
const AdminPlatformTaxonomy = lazy(() => import("@/pages/AdminPlatformTaxonomy"));
const AdminIntegrations = lazy(() => import("@/pages/AdminIntegrations"));
const AiMarketingManager = lazy(() => import("@/pages/AiMarketingManager"));
const BecomePartner = lazy(() => import("@/pages/BecomePartner"));
const PartnerLogin = lazy(() => import("@/pages/PartnerLogin"));
const AdminPartners = lazy(() => import("@/pages/AdminPartners"));
const PartnerDashboard = lazy(() => import("@/pages/PartnerDashboard"));
const AdminPartnerConfig = lazy(() => import("@/pages/AdminPartnerConfig"));
const EmployeeDashboard = lazy(() => import("@/pages/EmployeeDashboard"));
const FinanceHub = lazy(() => import("@/pages/FinanceHub"));
const InsuranceRenewal = lazy(() => import("@/pages/InsuranceRenewal"));
const InsuranceHub     = lazy(() => import("@/pages/InsuranceHub"));
const ServiceProviderApply = lazy(() => import("@/pages/ServiceProviderApply"));
const LogisticsHub = lazy(() => import("@/pages/LogisticsHub"));
const BrandsPage = lazy(() => import("@/pages/BrandsPage"));
const BrandStorePage = lazy(() => import("@/pages/BrandStorePage"));
const PartnerApplyUnified = lazy(() => import("@/pages/PartnerApplyUnified"));
const AdminLegalLedger = lazy(() => import("@/pages/AdminLegalLedger"));
const IntelHubLanding = lazy(() => import("@/pages/IntelHubLanding"));
const IntelCalculatorsIndex = lazy(() => import("@/pages/IntelCalculators").then((m) => ({ default: m.IntelCalculatorsIndex })));
const IntelCalculatorRunner  = lazy(() => import("@/pages/IntelCalculators").then((m) => ({ default: m.IntelCalculatorRunner })));
const IntelRates   = lazy(() => import("@/pages/IntelRates"));
const IntelContent = lazy(() => import("@/pages/IntelContent"));
const AdminCommunicationCenter = lazy(() => import("@/pages/AdminCommunicationCenter"));
const AdminCrmTemplatesPreview = lazy(() => import("@/pages/AdminCrmTemplatesPreview"));
const AdminInboundEmails = lazy(() => import("@/pages/AdminInboundEmails"));
const AdminMarketplaceListings = lazy(() => import("@/pages/AdminMarketplaceListings"));
const UsedBrowse = lazy(() => import("@/pages/MarketplaceListings").then((m) => ({ default: m.UsedBrowse })));
const UsedDetail = lazy(() => import("@/pages/MarketplaceListings").then((m) => ({ default: m.UsedDetail })));
const UsedCreate = lazy(() => import("@/pages/MarketplaceListings").then((m) => ({ default: m.UsedCreate })));
const ScrapBrowse = lazy(() => import("@/pages/MarketplaceListings").then((m) => ({ default: m.ScrapBrowse })));
const ScrapDetail = lazy(() => import("@/pages/MarketplaceListings").then((m) => ({ default: m.ScrapDetail })));
const ScrapCreate = lazy(() => import("@/pages/MarketplaceListings").then((m) => ({ default: m.ScrapCreate })));
const MyWatchlist = lazy(() => import("@/pages/MyWatchlist"));
const SellerOnboarding = lazy(() => import("@/pages/SellerOnboarding"));
const Experts = lazy(() => import("@/pages/Experts"));
const BecomeExpert = lazy(() => import("@/pages/BecomeExpert"));
const RateConsultation = lazy(() => import("@/pages/RateConsultation"));
const AssetRouteHistory = lazy(() => import("@/pages/AssetRouteHistory"));
const AmcLanding = lazy(() => import("@/pages/ServiceLanding").then((m) => ({ default: m.AmcLanding })));
const FmcLanding = lazy(() => import("@/pages/ServiceLanding").then((m) => ({ default: m.FmcLanding })));
const BreakdownLanding = lazy(() => import("@/pages/ServiceLanding").then((m) => ({ default: m.BreakdownLanding })));
const BuyCredits = lazy(() => import("@/pages/BuyCredits"));
const IndustrialMaterialsBrowse = lazy(() => import("@/pages/IndustrialMaterials").then((m) => ({ default: m.IndustrialMaterialsBrowse })));
const IndustrialProductDetail = lazy(() => import("@/pages/IndustrialMaterials").then((m) => ({ default: m.IndustrialProductDetail })));
const IndustrialSupplierRegister = lazy(() => import("@/pages/IndustrialForms").then((m) => ({ default: m.IndustrialSupplierRegister })));
const IndustrialProductCreate = lazy(() => import("@/pages/IndustrialForms").then((m) => ({ default: m.IndustrialProductCreate })));
const IndustrialRfq = lazy(() => import("@/pages/IndustrialForms").then((m) => ({ default: m.IndustrialRfq })));
const IndustrialBulkUpload = lazy(() => import("@/pages/IndustrialSellerHub").then((m) => ({ default: m.IndustrialBulkUploadReal })));
const IndustrialMyProducts = lazy(() => import("@/pages/IndustrialSellerHub").then((m) => ({ default: m.IndustrialMyProducts })));
const IndustrialRfqInbox = lazy(() => import("@/pages/IndustrialSellerHub").then((m) => ({ default: m.IndustrialRfqInbox })));
const RfqTracker = lazy(() => import("@/pages/RfqTracker"));
const AdminPushAnalytics = lazy(() => import("@/pages/AdminPushAnalytics"));
const AdminCrmQuotes = lazy(() => import("@/pages/AdminCrmQuotes"));
const AdminQuoteRepository = lazy(() => import("@/pages/AdminQuoteRepository"));
const AdminRevenueDashboard = lazy(() => import("@/pages/AdminRevenue"));
const AdminRevenueGovernance = lazy(() => import("@/pages/AdminRevenueGovernance"));
const AdminVerticalConfig = lazy(() => import("@/pages/AdminVerticalConfig"));
const AdminAllDeals = lazy(() => import("@/pages/AdminAllDeals"));
const AdminRevenueTabs = lazy(() => import("@/pages/AdminRevenueTabs"));
const AdminLiveOpsWatch = lazy(() => import("@/pages/AdminLiveOpsWatch"));
const AdminIncidentLog = lazy(() => import("@/pages/AdminIncidentLog"));
const VendorLeadsUnlock = lazy(() => import("@/pages/VendorLeadsUnlock"));
const AdminCrmAudit = lazy(() => import("@/pages/AdminCrmAudit"));
const AdminDataRoom = lazy(() => import("@/pages/AdminDataRoom"));
const AdminLauncher = lazy(() => import("@/pages/AdminLauncher"));
const AdminWhatsApp = lazy(() => import("@/pages/AdminWhatsApp"));
const VendorFanoutConsole = lazy(() => import("@/pages/VendorFanoutConsole"));
const AdminEmailBudget = lazy(() => import("@/pages/AdminEmailBudget"));
const CustomerApproval = lazy(() => import("@/pages/CustomerApproval"));
const VendorPortal = lazy(() => import("@/pages/VendorPortal"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
const AdminTenants = lazy(() => import("@/pages/AdminTenants"));
const AdminFleet = lazy(() => import("@/pages/AdminFleet"));
const AdminMaintenance = lazy(() => import("@/pages/AdminMaintenance"));
const AdminSpareParts = lazy(() => import("@/pages/AdminSpareParts"));
const AdminCompliance = lazy(() => import("@/pages/AdminCompliance"));
const AdminEconomics = lazy(() => import("@/pages/AdminEconomics"));
const AdminQrSheet = lazy(() => import("@/pages/AdminQrSheet"));
const AdminTenantNew = lazy(() => import("@/pages/AdminTenantNew"));
const AdminApprovalWorkflow = lazy(() => import("@/pages/AdminApprovalWorkflow"));
const AdminCompanyProfile = lazy(() => import("@/pages/AdminCompanyProfile"));
const AssetPublic = lazy(() => import("@/pages/AssetPublic"));
const TenantLogin = lazy(() => import("@/pages/TenantLogin"));
const ProductLanding = lazy(() => import("@/pages/ProductLanding"));
const FleetCloudLanding = lazy(() => import("@/pages/product/FleetCloudLanding"));
const MaintenanceCloudLanding = lazy(() => import("@/pages/product/MaintenanceCloudLanding"));
const CustomerOverview = lazy(() => import("@/pages/CustomerDashboard").then((m) => ({ default: m.CustomerOverview })));
const CustomerRequirements = lazy(() => import("@/pages/CustomerDashboard").then((m) => ({ default: m.CustomerRequirements })));
const CustomerQuotes = lazy(() => import("@/pages/CustomerDashboard").then((m) => ({ default: m.CustomerQuotes })));
const CustomerProfile = lazy(() => import("@/pages/CustomerDashboard").then((m) => ({ default: m.CustomerProfile })));
const TenantReports = lazy(() => import("@/pages/tenant/Reports"));
const TenantSmartFleet = lazy(() => import("@/pages/tenant/SmartFleet"));
const FleetTelemetry   = lazy(() => import("@/pages/tenant/FleetTelemetry"));
const MyOpportunities  = lazy(() => import("@/pages/tenant/MyOpportunities"));
const RoleTileDashboard = lazy(() => import("@/pages/dashboard/RoleTileDashboard"));
const VendorOverview = lazy(() => import("@/pages/VendorDashboard").then((m) => ({ default: m.VendorOverview })));
const VendorListings = lazy(() => import("@/pages/VendorDashboard").then((m) => ({ default: m.VendorListings })));
const VendorLeads = lazy(() => import("@/pages/VendorDashboard").then((m) => ({ default: m.VendorLeads })));
const VendorProfile = lazy(() => import("@/pages/VendorDashboard").then((m) => ({ default: m.VendorProfile })));
const AdminOverview = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminOverview })));
const AdminUsers = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminUsers })));
const AdminListings = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminListings })));
const AdminAuctions = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminAuctions })));
const AdminLeads = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminLeads })));
const AdminVendorDirectory = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminVendorDirectory })));
const AdminBriefing = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminBriefing })));
const AdminStateDashboard = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminStateDashboard })));
const AdminWorkforce = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminWorkforce })));
const AdminInspection = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminInspection })));
const AdminDeals = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminDeals })));
const AdminRevenue = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminRevenue })));
const AdminFounder = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminFounder })));
const AdminSpares = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminSpares })));

const FounderClub = lazy(() => import("@/pages/FounderClub"));
const EquipmentHubAiLanding = lazy(() => import("@/pages/EquipmentHubAiLanding"));

function Public({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

// Pass-through wrapper. Role-gating happens at the parent ProtectedRoute level.
function AdminOnly({ children }) {
  return children;
}

// Route guard driven by an admin-toggleable feature flag. When the flag is off,
// navigating to the page shows the 404 fallback instead of leaking the page.
function FeatureRoute({ flag, children }) {
  const { flags } = useSettings();
  if (!flags[flag]) {
    return (
      <Public>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="font-display font-black text-7xl text-ehi-primary">404</div>
          <p className="mt-4 text-slate-600">Page not found. <a href="/" className="text-ehi-accent underline">Back to home</a></p>
        </div>
      </Public>
    );
  }
  return children;
}

function PageFallback() {
  return (
    <div className="min-h-[50vh] grid place-items-center text-slate-400 text-sm" data-testid="route-loading">
      <div className="animate-pulse">Loading…</div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Public><Home /></Public>} />
        <Route path="/marketplace" element={<Public><Marketplace /></Public>} />
        <Route path="/categories" element={<Public><Categories /></Public>} />
        <Route path="/category/:slug" element={<Public><CategoryLanding /></Public>} />
        <Route path="/buy-equipment" element={<Public><BuyEquipment /></Public>} />
        <Route path="/sell-equipment" element={<Public><SellEquipment /></Public>} />
        <Route path="/reverse-auction" element={<Public><ReverseAuction /></Public>} />
        <Route path="/tools/:slug" element={<Public><SelectorPage /></Public>} />
        <Route path="/tools" element={<Public><SelectorPage /></Public>} />
        <Route path="/buy-new" element={<Public><BuyNewMarketplace /></Public>} />
        <Route path="/buy-new/:id" element={<Public><BuyNewDetail /></Public>} />
        <Route path="/buy-new/become-dealer" element={<Public><BecomeDealer /></Public>} />
        <Route path="/buy-new/oem-partners" element={<Public><OEMPartners /></Public>} />
        <Route path="/buy-new/compare-quotes" element={<Public><BuyerQuoteComparison /></Public>} />
        <Route path="/dealer/dashboard" element={<Public><DealerDashboard /></Public>} />
        <Route path="/search" element={<Public><UniversalSearch /></Public>} />
        <Route path="/workforce" element={<Public><Workforce /></Public>} />
        <Route path="/inspection" element={<Public><Inspection /></Public>} />
        <Route path="/inspection/request" element={<Public><RequestInspection /></Public>} />
        <Route path="/inspection/providers" element={<Public><InspectionProviders /></Public>} />
        <Route path="/inspection/partner-register" element={<Public><BecomeInspectionPartner /></Public>} />
        <Route path="/spares" element={<Public><SparesLanding /></Public>} />
        <Route path="/spares/request" element={<Public><SparesRequest /></Public>} />
        <Route path="/spares/supplier-register" element={<Public><SparesSupplierRegister /></Public>} />
        {/* Used Equipment marketplace — full CRUD, moderation, CRM linkage */}
        <Route path="/used" element={<Public><UsedBrowse /></Public>} />
        <Route path="/used/:id" element={<Public><UsedDetail /></Public>} />
        <Route path="/sell-used-machinery" element={<Public><UsedCreate /></Public>} />
        <Route path="/create-used-listing" element={<Public><UsedCreate /></Public>} />
        {/* Industrial Scrap & Asset Disposal marketplace */}
        <Route path="/scrap" element={<Public><ScrapBrowse /></Public>} />
        <Route path="/scrap/:id" element={<Public><ScrapDetail /></Public>} />
        <Route path="/dispose-scrap" element={<Public><ScrapCreate /></Public>} />
        <Route path="/create-scrap-listing" element={<Public><ScrapCreate /></Public>} />
        <Route path="/become-scrap-buyer" element={<Public><BecomeScrapBuyer /></Public>} />
        <Route path="/scrap/become-buyer" element={<Public><BecomeScrapBuyer /></Public>} />
        <Route path="/careers" element={<Public><Careers /></Public>} />
        <Route path="/careers/:id" element={<Public><Careers /></Public>} />
        <Route path="/my-watchlist" element={<Public><MyWatchlist /></Public>} />
        <Route path="/seller/onboarding" element={<Public><SellerOnboarding /></Public>} />
        <Route path="/experts" element={<Public><Experts /></Public>} />
        <Route path="/experts/become" element={<Public><BecomeExpert /></Public>} />
        <Route path="/experts/rate" element={<Public><RateConsultation /></Public>} />
        <Route path="/fleet/:tenantId/assets/:assetId/route" element={<Public><AssetRouteHistory /></Public>} />
        {/* Service Network — AMC / FMC / Breakdown 24×7 public landings */}
        <Route path="/amc" element={<Public><AmcLanding /></Public>} />
        <Route path="/fmc" element={<Public><FmcLanding /></Public>} />
        <Route path="/breakdown" element={<Public><BreakdownLanding /></Public>} />
        <Route path="/buy-credits" element={<Public><BuyCredits /></Public>} />
        <Route path="/industrial" element={<Navigate to="/industrial-materials" replace />} />
        {/* Industrial Materials Hub · VRT005 · scalable procurement marketplace */}
        <Route path="/industrial-materials" element={<Public><IndustrialMaterialsBrowse /></Public>} />
        <Route path="/industrial-materials/product/:id" element={<Public><IndustrialProductDetail /></Public>} />
        <Route path="/industrial-materials/register-supplier" element={<Public><IndustrialSupplierRegister /></Public>} />
        <Route path="/industrial-materials/list-product" element={<Public><IndustrialProductCreate /></Public>} />
        <Route path="/industrial-materials/rfq" element={<Public><IndustrialRfq /></Public>} />
        <Route path="/industrial-materials/bulk-upload" element={<Public><IndustrialBulkUpload /></Public>} />
        <Route path="/industrial-materials/my-products" element={<Public><IndustrialMyProducts /></Public>} />
        <Route path="/industrial-materials/rfq-inbox" element={<Public><IndustrialRfqInbox /></Public>} />
        <Route path="/rfq/track/:code" element={<Public><RfqTracker /></Public>} />
        {/* Legacy /spares kept for backward-compat redirect */}
        <Route path="/spares/*" element={<Navigate replace to="/industrial-materials" />} />
        <Route path="/suppliers-directory" element={<Public><SuppliersDirectory /></Public>} />
        <Route path="/saas/signup" element={<Public><SaasSignup /></Public>} />
        <Route path="/saas/pricing" element={<Public><SaasPricing /></Public>} />
        <Route path="/coming-soon" element={<Public><ComingSoonIndex /></Public>} />
        <Route path="/coming-soon/:slug" element={<Public><ComingSoonDetail /></Public>} />
        <Route path="/privacy" element={<Public><Privacy /></Public>} />
        <Route path="/terms" element={<Public><Terms /></Public>} />
        <Route path="/refund-policy" element={<Public><RefundPolicy /></Public>} />
        <Route path="/refunds" element={<Public><RefundPolicy /></Public>} />
        <Route path="/supplier-agreement" element={<Public><SupplierAgreement /></Public>} />
        <Route path="/mission" element={<Public><MissionStatement /></Public>} />
        <Route path="/mission-statement" element={<Public><MissionStatement /></Public>} />
        <Route path="/equipment/:id" element={<Public><EquipmentDetail /></Public>} />
        <Route path="/auctions" element={<Public><Auctions /></Public>} />
        <Route path="/auctions/:id" element={<Public><AuctionDetail /></Public>} />
        <Route path="/post-requirement" element={<Public><PostRequirement /></Public>} />
        <Route path="/connect" element={<Public><Connect /></Public>} />
        <Route path="/connect/dashboard" element={<ProtectedRoute roles={["customer", "tenant_owner", "admin", "support", "vendor", "founder"]}><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<ConnectDashboard />} />
        </Route>
        <Route path="/connect/crm" element={<ProtectedRoute roles={["customer", "tenant_owner", "admin", "support", "vendor", "founder"]}><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<ConnectCRM />} />
        </Route>
        <Route path="/connect/branding" element={<ProtectedRoute roles={["customer", "tenant_owner", "admin", "support", "vendor", "founder"]}><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<ConnectBranding />} />
        </Route>
        <Route path="/supplier/hub" element={<ProtectedRoute roles={["customer", "tenant_owner", "admin", "support", "vendor", "founder"]}><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<SupplierBosHub />} />
        </Route>
        <Route path="/finance" element={<Public><FinanceHub /></Public>} />
        <Route path="/logistics" element={<Public><LogisticsHub /></Public>} />
        <Route path="/brands" element={<Public><BrandsPage /></Public>} />
        <Route path="/brands/:slug" element={<Public><BrandStorePage /></Public>} />
        <Route path="/become-partner" element={<Public><PartnerApplyUnified /></Public>} />
        <Route path="/oem/apply" element={<Public><PartnerApplyUnified /></Public>} />
        <Route path="/intel" element={<Public><IntelHubLanding /></Public>} />
        <Route path="/intel/calculators" element={<Public><IntelCalculatorsIndex /></Public>} />
        <Route path="/intel/calculators/:slug" element={<Public><IntelCalculatorRunner /></Public>} />
        <Route path="/intel/rates" element={<Public><IntelRates /></Public>} />
        <Route path="/intel/video/:slug" element={<Public><IntelContent /></Public>} />
        <Route path="/intel/safety_tip/:slug" element={<Public><IntelContent /></Public>} />
        <Route path="/intel/launch/:slug" element={<Public><IntelContent /></Public>} />
        <Route path="/intel/news/:slug" element={<Public><IntelContent /></Public>} />
        <Route path="/intel/community/:slug" element={<Public><IntelContent /></Public>} />
        <Route path="/rentals" element={<Public><Marketplace /></Public>} />
        <Route path="/become-service-provider" element={<Public><ServiceProviderApply /></Public>} />
        <Route path="/insurance" element={<Public><InsuranceHub /></Public>} />
        <Route path="/insurance/renewal" element={<Public><InsuranceRenewal /></Public>} />
        <Route path="/become-supplier" element={<Public><BecomeSupplier /></Public>} />
        <Route path="/pricing" element={<Public><Pricing /></Public>} />
        {/* Founder Club: hidden from public nav; page reachable via banner CTA only when the admin flag is on */}
        <Route path="/founder-club" element={<FeatureRoute flag="founder_club_visible"><Public><FounderClub /></Public></FeatureRoute>} />
        {/* EquipmentHub AI — public directory (SaaS preview). OFF by default; admin
            toggles `saas_public_directory` from /admin/config → Feature Flags when ready. */}
        <Route path="/ai-workforce" element={<FeatureRoute flag="saas_public_directory"><EquipmentHubAiLanding /></FeatureRoute>} />
        <Route path="/about" element={<Public><About /></Public>} />
        <Route path="/contact" element={<Public><Contact /></Public>} />
        <Route path="/blog" element={<Public><BlogList /></Public>} />
        <Route path="/blog/:slug" element={<Public><BlogPost /></Public>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/become-partner" element={<Public><BecomePartner /></Public>} />
        <Route path="/partner-login" element={<PartnerLogin />} />
        <Route path="/partner/dashboard" element={<PartnerDashboard />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Stage B.5 — Pilot Tenant Login (backend returns 404 until saas_multi_tenant flag ON) */}
        <Route path="/tenant/login" element={<TenantLogin />} />

        {/* Stage B.4 — Public QR landing (backend returns 404 until qr_code flag ON) */}
        <Route path="/asset/:tenantId/:assetId" element={<AssetPublic />} />

        {/* Stage B.11 — Products mega-menu landing pages */}
        <Route path="/product/fleet-cloud" element={<PublicLayout><FleetCloudLanding /></PublicLayout>} />
        <Route path="/product/maintenance-cloud" element={<PublicLayout><MaintenanceCloudLanding /></PublicLayout>} />
        <Route path="/product/:slug" element={<PublicLayout><ProductLanding /></PublicLayout>} />
        <Route path="/partner/amc-onboard" element={<PublicLayout><AmcPartnerOnboard /></PublicLayout>} />
        <Route path="/partner/breakdown-onboard" element={<PublicLayout><BreakdownPartnerOnboard /></PublicLayout>} />
        {/* /intel is served by IntelHubLanding above; keep directory + reader on sub-paths */}
        <Route path="/intel/directory" element={<PublicLayout><IntelDirectory /></PublicLayout>} />
        <Route path="/intel/reports/:slug" element={<PublicLayout><IntelReader /></PublicLayout>} />
        <Route path="/approval/:token" element={<CustomerApproval />} />
        <Route path="/vendor-portal/:token" element={<VendorPortal />} />

        <Route
          path="/dashboard"
          element={<ProtectedRoute roles={["customer", "tenant_owner", "admin", "support"]}><DashboardLayout /></ProtectedRoute>}
        >
          <Route index element={<RoleTileDashboard />} />
          <Route path="home" element={<RoleTileDashboard />} />
          <Route path="classic" element={<CustomerOverview />} />
          <Route path="requirements" element={<CustomerRequirements />} />
          <Route path="quotes" element={<CustomerQuotes />} />
          <Route path="reports" element={<TenantReports />} />
          <Route path="opportunities" element={<MyOpportunities />} />
          <Route path="smart-fleet" element={<TenantSmartFleet />} />
          <Route path="fleet-telemetry" element={<FleetTelemetry />} />
          <Route path="profile" element={<CustomerProfile />} />
          <Route path="notifications" element={<NotificationSettings />} />
        </Route>

        <Route
          path="/vendor"
          element={<ProtectedRoute roles={["vendor", "admin"]}><DashboardLayout /></ProtectedRoute>}
        >
          <Route index element={<RoleTileDashboard />} />
          <Route path="home" element={<VendorOverview />} />
          <Route path="listings" element={<VendorListings />} />
          <Route path="leads" element={<VendorLeadsUnlock />} />
          <Route path="leads-legacy" element={<VendorLeads />} />
          <Route path="profile" element={<VendorProfile />} />
        </Route>

        <Route
          path="/admin"
          element={<ProtectedRoute roles={["admin", "support"]}><DashboardLayout /></ProtectedRoute>}
        >
          <Route index element={<RoleTileDashboard />} />
          <Route path="launcher" element={<AdminLauncher />} />
          <Route path="whatsapp" element={<AdminOnly><AdminWhatsApp /></AdminOnly>} />
          <Route path="leads/:reqId/fanout" element={<AdminOnly><VendorFanoutConsole /></AdminOnly>} />
          <Route path="home" element={<AdminOverview />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="listings" element={<AdminListings />} />
          <Route path="vendor-directory" element={<AdminVendorDirectory />} />
          <Route path="clients" element={<AdminOnly><AdminClients /></AdminOnly>} />
          <Route path="clients/analytics" element={<AdminOnly><AdminClientAnalytics /></AdminOnly>} />
          <Route path="connect/plans" element={<AdminOnly><AdminConnectPlans /></AdminOnly>} />
          <Route path="clients/bulk-import" element={<AdminOnly><AdminClientsBulkImport /></AdminOnly>} />
          <Route path="clients/:id" element={<AdminOnly><AdminClientDetail /></AdminOnly>} />
          <Route path="requirement-entry" element={<AdminOnly><AdminRequirementEntry /></AdminOnly>} />
          <Route path="briefing" element={<AdminBriefing />} />
          <Route path="state/:state" element={<AdminStateDashboard />} />
          <Route path="buy-new" element={<AdminBuyNew />} />
          <Route path="workforce" element={<AdminWorkforce />} />
          <Route path="inspection" element={<AdminInspection />} />
          <Route path="spares" element={<AdminSpares />} />
          <Route path="onboard" element={<AdminOnboard />} />
          <Route path="approvals" element={<AdminOnly><AdminApprovals /></AdminOnly>} />
          <Route path="amc" element={<AdminOnly><AdminAmc /></AdminOnly>} />
          <Route path="breakdown" element={<AdminOnly><AdminBreakdown /></AdminOnly>} />
          <Route path="intel" element={<AdminOnly><AdminIntel /></AdminOnly>} />
          <Route path="team" element={<AdminOnly><AdminTeam /></AdminOnly>} />
          <Route path="employees" element={<AdminOnly><AdminEmployees /></AdminOnly>} />
          <Route path="bulk-upload" element={<AdminOnly><AdminBulkUpload /></AdminOnly>} />
          <Route path="invoices" element={<AdminOnly><AdminInvoices /></AdminOnly>} />
          <Route path="permissions" element={<AdminOnly><AdminPermissions /></AdminOnly>} />
          <Route path="security" element={<AdminOnly><AdminSecurity /></AdminOnly>} />
          <Route path="health-check" element={<AdminOnly><AdminHealthCheck /></AdminOnly>} />
          <Route path="foundation" element={<AdminOnly><AdminFoundation /></AdminOnly>} />
          <Route path="hrms" element={<AdminOnly><AdminHrms /></AdminOnly>} />
          <Route path="legal-ledger" element={<AdminOnly><AdminLegalLedger /></AdminOnly>} />
          <Route path="my-dashboard" element={<EmployeeDashboard />} />
          <Route path="platform-taxonomy" element={<AdminOnly><AdminPlatformTaxonomy /></AdminOnly>} />
          <Route path="integrations" element={<AdminOnly><AdminIntegrations /></AdminOnly>} />
          <Route path="marketplace-listings" element={<AdminOnly><AdminMarketplaceListings /></AdminOnly>} />
          <Route path="push-analytics" element={<AdminOnly><AdminPushAnalytics /></AdminOnly>} />
          <Route path="crm-quotes" element={<AdminOnly><AdminCrmQuotes /></AdminOnly>} />
          <Route path="quote-repository" element={<AdminOnly><AdminQuoteRepository /></AdminOnly>} />
          <Route path="revenue-dashboard" element={<AdminOnly><AdminRevenueDashboard /></AdminOnly>} />
          <Route path="revenue-governance" element={<AdminOnly><AdminRevenueGovernance /></AdminOnly>} />
          <Route path="revenue-tabs" element={<AdminOnly><AdminRevenueTabs /></AdminOnly>} />
          <Route path="vertical-config" element={<AdminOnly><AdminVerticalConfig /></AdminOnly>} />
          <Route path="all-deals" element={<AdminOnly><AdminAllDeals /></AdminOnly>} />
          <Route path="live-ops" element={<AdminOnly><AdminLiveOpsWatch /></AdminOnly>} />
          <Route path="incidents" element={<AdminOnly><AdminIncidentLog /></AdminOnly>} />
          <Route path="scrap/buyers" element={<AdminOnly><AdminScrapBuyers /></AdminOnly>} />
          <Route path="careers" element={<AdminOnly><AdminCareers /></AdminOnly>} />
          <Route path="crm-audit" element={<AdminOnly><AdminCrmAudit /></AdminOnly>} />
          <Route path="data-room" element={<AdminOnly><AdminDataRoom /></AdminOnly>} />
          <Route path="crm-email-budget" element={<AdminOnly><AdminEmailBudget /></AdminOnly>} />
          <Route path="access-requests" element={<AdminAccessRequests />} />
          <Route path="offers" element={<AdminOnly><AdminOffers /></AdminOnly>} />
          <Route path="ads" element={<AdminOnly><AdminAds /></AdminOnly>} />
          <Route path="credits" element={<AdminOnly><AdminCredits /></AdminOnly>} />
          <Route path="config" element={<AdminOnly><AdminConfig /></AdminOnly>} />
          <Route path="crm-automation" element={<AdminOnly><AdminCrmAutomation /></AdminOnly>} />
          <Route path="ai-workforce" element={<AdminOnly><AdminAiWorkforce /></AdminOnly>} />
          <Route path="ai-marketing" element={<AdminOnly><AiMarketingManager /></AdminOnly>} />
          <Route path="partners" element={<AdminOnly><AdminPartners /></AdminOnly>} />
          <Route path="partner-config" element={<AdminOnly><AdminPartnerConfig /></AdminOnly>} />
          <Route path="communication-center" element={<AdminOnly><AdminCommunicationCenter /></AdminOnly>} />
          <Route path="crm-templates-preview" element={<AdminOnly><AdminCrmTemplatesPreview /></AdminOnly>} />
          <Route path="inbound-emails" element={<AdminOnly><AdminInboundEmails /></AdminOnly>} />
          <Route path="tenants" element={<AdminOnly><AdminTenants /></AdminOnly>} />
          <Route path="tenants/new" element={<AdminOnly><AdminTenantNew /></AdminOnly>} />
          <Route path="fleet/:tenantId" element={<AdminOnly><AdminFleet /></AdminOnly>} />
          <Route path="fleet/:tenantId/telemetry" element={<AdminOnly><FleetTelemetry /></AdminOnly>} />
          <Route path="fleet/:tenantId/qr-sheet" element={<AdminOnly><AdminQrSheet /></AdminOnly>} />
          <Route path="fleet/:tenantId/economics" element={<AdminOnly><AdminEconomics /></AdminOnly>} />
          <Route path="fleet/:tenantId/spare-parts" element={<AdminOnly><AdminSpareParts /></AdminOnly>} />
          <Route path="maintenance/:tenantId/spare-parts" element={<AdminOnly><AdminSpareParts /></AdminOnly>} />
          <Route path="compliance/:tenantId" element={<AdminOnly><AdminCompliance /></AdminOnly>} />
          <Route path="approval-queue" element={<AdminOnly><AdminApprovalWorkflow /></AdminOnly>} />
          <Route path="company-profile" element={<AdminOnly><AdminCompanyProfile /></AdminOnly>} />
          <Route path="maintenance/:tenantId" element={<AdminOnly><AdminMaintenance /></AdminOnly>} />
          <Route path="deals" element={<AdminDeals />} />
          <Route path="revenue" element={<AdminOnly><AdminRevenue /></AdminOnly>} />
          <Route path="founder" element={<AdminOnly><AdminFounder /></AdminOnly>} />
          <Route path="auctions" element={<AdminAuctions />} />
        </Route>

        <Route path="*" element={<Public>
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <div className="font-display font-black text-7xl text-ehi-primary">404</div>
            <p className="mt-4 text-slate-600">Page not found. <a href="/" className="text-ehi-accent underline">Back to home</a></p>
          </div>
        </Public>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <I18nProvider>
              <AppRouter />
              <Toaster richColors position="top-right" />
              <InstallPrompt />
              <PwaUpdateToast />
            </I18nProvider>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
